import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TableSkeleton } from '../../components/enterprise';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Download as DownloadIcon,
  PlayArrow as RunIcon,
  Refresh as RefreshIcon,
  RestartAlt as RestoreIcon,
  Delete as DeleteIcon,
  CloudUpload as InstallIcon,
} from '@mui/icons-material';
import apiClient from '../../services/apiClient';
import HelpIcon from '../../components/help/HelpIcon';
import { toServerDate } from '../../timezone';

const BASE = '/api/infrastructure/clickhouse-backups';

const jobStatusColor = (s) => ({
  PENDING: 'default',
  RUNNING: 'info',
  SUCCESS: 'success',
  FAILED: 'error',
}[s] || 'default');

const fmt = (d) => (toServerDate(d)?.toLocaleString() ?? '—');

const Stat = ({ label, value, valueColor }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="h6" sx={{ color: valueColor }}>{value ?? '—'}</Typography>
  </Box>
);

const fmtBytes = (n) => {
  if (n == null) return '—';
  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB'];
  let v = n, i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 10 ? 0 : 1)} ${units[i]}`;
};

const ClickHouseBackupsTab = ({ clusters }) => {
  const [clusterId, setClusterId] = useState('');
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configDirty, setConfigDirty] = useState({});
  const [saving, setSaving] = useState(false);
  const [walStats, setWalStats] = useState(null);

  const [installing, setInstalling] = useState(false);
  const [creating, setCreating] = useState(false);

  const [backups, setBackups] = useState([]);
  const [backupsLoading, setBackupsLoading] = useState(false);

  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);

  const [message, setMessage] = useState(null);

  const [restoreDialog, setRestoreDialog] = useState({ open: false, name: '', downloadFirst: false, confirm: '' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, name: '', local: true, remote: true, confirm: '' });

  const selectedCluster = useMemo(
    () => clusters?.find((c) => String(c.id) === String(clusterId)),
    [clusters, clusterId]
  );

  // Auto-select first cluster if none chosen yet
  useEffect(() => {
    if (!clusterId && clusters && clusters.length > 0) {
      setClusterId(String(clusters[0].id));
    }
  }, [clusters, clusterId]);

  const loadAll = useCallback(async () => {
    if (!clusterId) return;
    setConfigLoading(true);
    try {
      const res = await apiClient.get(`${BASE}/clusters/${clusterId}/config`);
      setConfig(res.data);
      setConfigDirty({});
    } catch (e) {
      setMessage({ type: 'error', text: `Load config failed: ${e?.response?.data?.error || e.message}` });
    } finally {
      setConfigLoading(false);
    }
    // Load jobs in parallel
    setJobsLoading(true);
    try {
      const res = await apiClient.get(`${BASE}/clusters/${clusterId}/jobs?size=25`);
      setJobs(res.data?.content || []);
    } catch (e) {
      // 404 is fine — no jobs yet
    } finally {
      setJobsLoading(false);
    }
  }, [clusterId]);

  const loadBackups = useCallback(async () => {
    if (!clusterId) return;
    setBackupsLoading(true);
    setBackups([]);
    try {
      const res = await apiClient.get(`${BASE}/clusters/${clusterId}/list`);
      setBackups(res.data || []);
    } catch (e) {
      setMessage({ type: 'error', text: `List failed: ${e?.response?.data?.error || e.message}` });
    } finally {
      setBackupsLoading(false);
    }
  }, [clusterId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Auto-refresh WAL stats every 10 s so operators see the write-durability
  // health moving in real time.
  useEffect(() => {
    let cancelled = false;
    const fetchWal = async () => {
      try {
        const res = await apiClient.get('/api/v1.0/admin/clickhouse/wal-stats');
        if (!cancelled) setWalStats(res.data);
      } catch {
        // Endpoint missing or 401 — silently skip. The rest of the tab still works.
      }
    };
    fetchWal();
    const t = setInterval(fetchWal, 10_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const set = (k, v) => setConfigDirty((d) => ({ ...d, [k]: v }));
  const cur = (k) => (configDirty[k] !== undefined ? configDirty[k] : config?.[k]);

  const saveConfig = async () => {
    if (!clusterId) return;
    setSaving(true);
    try {
      await apiClient.put(`${BASE}/clusters/${clusterId}/config`, configDirty);
      setMessage({ type: 'success', text: 'Backup configuration saved.' });
      await loadAll();
    } catch (e) {
      setMessage({ type: 'error', text: `Save failed: ${e?.response?.data?.error || e.message}` });
    } finally {
      setSaving(false);
    }
  };

  const installTool = async () => {
    setInstalling(true);
    try {
      const res = await apiClient.post(`${BASE}/clusters/${clusterId}/install-tool`);
      const { nodesInstalled, nodesAttempted, toolInstalled } = res.data || {};
      setMessage({
        type: toolInstalled ? 'success' : 'warning',
        text: `Install: ${nodesInstalled}/${nodesAttempted} node(s) ready${toolInstalled ? '' : ' — see per-node detail'}`,
      });
      await loadAll();
    } catch (e) {
      setMessage({ type: 'error', text: `Install failed: ${e?.response?.data?.error || e.message}` });
    } finally {
      setInstalling(false);
    }
  };

  const runNow = async () => {
    setCreating(true);
    try {
      const res = await apiClient.post(`${BASE}/clusters/${clusterId}/create`);
      const j = res.data || {};
      setMessage({
        type: j.status === 'SUCCESS' ? 'success' : 'error',
        text: `Backup ${j.backupName}: ${j.status}${j.errorMessage ? ' — ' + j.errorMessage : ''}`,
      });
      await loadAll();
      await loadBackups();
    } catch (e) {
      setMessage({ type: 'error', text: `Backup failed: ${e?.response?.data?.error || e.message}` });
    } finally {
      setCreating(false);
    }
  };

  const openRestore = (name, downloadFirst = false) => {
    setRestoreDialog({ open: true, name, downloadFirst, confirm: '' });
  };
  const doRestore = async () => {
    if (restoreDialog.confirm !== selectedCluster?.name) {
      setMessage({ type: 'error', text: 'Confirm text must equal the cluster name exactly.' });
      return;
    }
    try {
      const res = await apiClient.post(
        `${BASE}/clusters/${clusterId}/restore`,
        null,
        {
          params: {
            backupName: restoreDialog.name,
            confirm: restoreDialog.confirm,
            downloadFirst: restoreDialog.downloadFirst,
          },
        }
      );
      const j = res.data || {};
      setMessage({
        type: j.status === 'SUCCESS' ? 'success' : 'error',
        text: `Restore ${j.backupName}: ${j.status}${j.errorMessage ? ' — ' + j.errorMessage : ''}`,
      });
      setRestoreDialog({ open: false, name: '', downloadFirst: false, confirm: '' });
      await loadAll();
    } catch (e) {
      setMessage({ type: 'error', text: `Restore failed: ${e?.response?.data?.error || e.message}` });
    }
  };

  const openDelete = (name) => {
    setDeleteDialog({ open: true, name, local: true, remote: true, confirm: '' });
  };
  const doDelete = async () => {
    if (deleteDialog.confirm !== selectedCluster?.name) {
      setMessage({ type: 'error', text: 'Confirm text must equal the cluster name exactly.' });
      return;
    }
    try {
      await apiClient.delete(
        `${BASE}/clusters/${clusterId}/${encodeURIComponent(deleteDialog.name)}`,
        {
          params: {
            confirm: deleteDialog.confirm,
            local: deleteDialog.local,
            remote: deleteDialog.remote,
          },
        }
      );
      setMessage({ type: 'success', text: `Deleted ${deleteDialog.name}` });
      setDeleteDialog({ open: false, name: '', local: true, remote: true, confirm: '' });
      await loadAll();
      await loadBackups();
    } catch (e) {
      setMessage({ type: 'error', text: `Delete failed: ${e?.response?.data?.error || e.message}` });
    }
  };

  if (!clusters || clusters.length === 0) {
    return (
      <Alert severity="info">
        Create a cluster first — backup operations work per-cluster.
      </Alert>
    );
  }

  return (
    <Box>
      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* Cluster picker */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="subtitle2">Cluster:</Typography>
          <Select
            size="small"
            value={clusterId}
            onChange={(e) => setClusterId(e.target.value)}
            sx={{ minWidth: 240 }}
          >
            {clusters.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                {c.name} <Chip size="small" sx={{ ml: 1 }} label={c.status} />
              </MenuItem>
            ))}
          </Select>
          <Button size="small" onClick={loadAll} startIcon={<RefreshIcon />}>Refresh</Button>
          <Box sx={{ flex: 1 }} />
          <HelpIcon docKey="backups_tab" />
        </Stack>
      </Paper>

      {/* Write durability tile (WAL + circuit breaker health) */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="h6">Write durability (live)</Typography>
          <HelpIcon docKey="wal_durability" />
          <Box sx={{ flex: 1 }} />
          {walStats
            ? (walStats.pendingWalFiles > 0
                ? <Chip label={`${walStats.pendingWalFiles} WAL file(s) queued`} color="warning" size="small" />
                : <Chip label="WAL clean" color="success" size="small" />)
            : <Chip label="loading…" size="small" />}
        </Stack>
        {walStats ? (
          <Stack direction="row" spacing={4} sx={{ flexWrap: 'wrap' }}>
            <Stat label="Pending WAL files" value={walStats.pendingWalFiles} />
            <Stat label="Rows buffered in WAL" value={walStats.rowsInWal} />
            <Stat label="WAL writes" value={walStats.walFilesWritten} />
            <Stat label="WAL replayed" value={walStats.walFilesReplayed} />
            <Stat label="Dead-lettered" value={walStats.walFilesDeadLettered}
                  valueColor={walStats.walFilesDeadLettered > 0 ? 'error.main' : undefined} />
            <Box>
              <Typography variant="caption" color="text.secondary">Per-cluster circuit</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                {Object.entries(walStats.circuits || {}).map(([k, v]) => {
                  const state = (v || '').split(' ')[0];
                  const color = state === 'CLOSED' ? 'success'
                    : state === 'HALF_OPEN' ? 'warning'
                    : state === 'OPEN' ? 'error' : 'default';
                  return (
                    <Chip key={k} size="small" color={color}
                          label={k === '0' ? `default: ${state}` : `cluster ${k}: ${state}`} />
                  );
                })}
                {Object.keys(walStats.circuits || {}).length === 0 && (
                  <Typography variant="caption" color="text.secondary">no activity yet</Typography>
                )}
              </Stack>
            </Box>
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">Loading…</Typography>
        )}
      </Paper>

      {/* Tool install / enabled state */}
      {config && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
            <Typography variant="h6">Tool & Run</Typography>
            <HelpIcon docKey="install_backup_tool" />
            <Box sx={{ flex: 1 }} />
            {config.backupToolInstalled ? (
              <Chip label="Tool installed" color="success" size="small" />
            ) : (
              <Chip label="Tool not installed" color="warning" size="small" />
            )}
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={installing ? <CircularProgress size={14} /> : <InstallIcon />}
              onClick={installTool}
              disabled={installing}
            >
              {config.backupToolInstalled ? 'Reinstall / refresh tool' : 'Install backup tool'}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={creating ? <CircularProgress size={14} color="inherit" /> : <RunIcon />}
              onClick={runNow}
              disabled={!config.backupToolInstalled || creating}
            >
              Run backup now
            </Button>
            <HelpIcon docKey="run_backup_now" />
          </Stack>
        </Paper>
      )}

      {/* Backup schedule / S3 */}
      {config && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="h6">Schedule & Storage</Typography>
            <HelpIcon docKey="backup_schedule" />
          </Stack>

          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!cur('backupEnabled')}
                    onChange={(e) => set('backupEnabled', e.target.checked)}
                  />
                }
                label="Scheduled backups enabled"
              />
              <TextField
                size="small"
                label="Cron (6-field)"
                placeholder="0 0 2 * * *"
                value={cur('backupSchedule') || ''}
                onChange={(e) => set('backupSchedule', e.target.value)}
                sx={{ minWidth: 220 }}
                helperText="sec min hour dom mon dow"
              />
              <TextField
                size="small"
                type="number"
                label="Retention (days)"
                value={cur('backupRetentionDays') ?? 30}
                onChange={(e) => set('backupRetentionDays', Number(e.target.value))}
                sx={{ width: 160 }}
              />
            </Stack>

            <Divider>
              <Chip label="S3 (optional)" size="small" />
              <HelpIcon docKey="backup_s3" />
            </Divider>

            <Stack direction="row" spacing={2}>
              <TextField
                size="small" label="Bucket" value={cur('backupS3Bucket') || ''}
                onChange={(e) => set('backupS3Bucket', e.target.value)}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small" label="Region" value={cur('backupS3Region') || ''}
                onChange={(e) => set('backupS3Region', e.target.value)}
                sx={{ width: 140 }}
              />
              <TextField
                size="small" label="Endpoint (optional)" value={cur('backupS3Endpoint') || ''}
                onChange={(e) => set('backupS3Endpoint', e.target.value)}
                sx={{ flex: 1 }}
                helperText="Set for MinIO / non-AWS S3"
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                size="small" label="Access key" value={cur('backupS3AccessKey') || ''}
                onChange={(e) => set('backupS3AccessKey', e.target.value)}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small" type="password" label="Secret key (leave blank to keep)"
                value={cur('backupS3SecretKey') || ''}
                onChange={(e) => set('backupS3SecretKey', e.target.value)}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small" label="Prefix" value={cur('backupS3Prefix') || ''}
                onChange={(e) => set('backupS3Prefix', e.target.value)}
                sx={{ width: 220 }}
                helperText="e.g. backups/prod"
              />
            </Stack>

            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={saveConfig}
                disabled={Object.keys(configDirty).length === 0 || saving}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
              <Button disabled={Object.keys(configDirty).length === 0}
                      onClick={() => setConfigDirty({})}>
                Discard changes
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {/* Existing backups */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="h6">Available backups</Typography>
          <HelpIcon docKey="backups_list" />
          <Box sx={{ flex: 1 }} />
          <Button size="small" startIcon={<DownloadIcon />} onClick={loadBackups}>
            Query backups
          </Button>
        </Stack>
        {backupsLoading ? (
          <TableSkeleton rows={5} columns={4} />
        ) : backups.length === 0 ? (
          <Alert severity="info">
            No backups listed yet. Click "Query backups" to invoke
            <code style={{ marginLeft: 4 }}>clickhouse-backup list</code> on the cluster.
          </Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {backups.map((b, i) => (
                <TableRow key={`${b.name}_${b.location}_${i}`}>
                  <TableCell>{b.name}</TableCell>
                  <TableCell>{b.created || '—'}</TableCell>
                  <TableCell>{b.size || '—'}</TableCell>
                  <TableCell>
                    <Chip size="small"
                          label={b.location}
                          color={b.location === 'remote' ? 'primary' : 'default'} />
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small" color="warning" startIcon={<RestoreIcon />}
                      onClick={() => openRestore(b.name, b.location === 'remote')}
                    >
                      Restore
                    </Button>
                    <Button
                      size="small" color="error" startIcon={<DeleteIcon />}
                      onClick={() => openDelete(b.name)}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Job history */}
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="h6">Recent jobs</Typography>
          <HelpIcon docKey="backup_jobs" />
        </Stack>
        {jobsLoading ? (
          <CircularProgress size={20} />
        ) : jobs.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No jobs yet.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Kind</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Remote?</TableCell>
                <TableCell>By</TableCell>
                <TableCell>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((j) => (
                <TableRow key={j.id}>
                  <TableCell>{fmt(j.createdAt)}</TableCell>
                  <TableCell>{j.kind}</TableCell>
                  <TableCell>{j.backupName}</TableCell>
                  <TableCell>
                    <Chip label={j.status} color={jobStatusColor(j.status)} size="small" />
                  </TableCell>
                  <TableCell>{fmtBytes(j.sizeBytes)}</TableCell>
                  <TableCell>{j.hasRemote ? 'yes' : '—'}</TableCell>
                  <TableCell>{j.initiatedBy}</TableCell>
                  <TableCell style={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                             title={j.errorMessage || ''}>
                    {j.errorMessage || '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Restore dialog */}
      <Dialog open={restoreDialog.open} onClose={() => setRestoreDialog({ ...restoreDialog, open: false })}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            Restore backup: {restoreDialog.name}
            <HelpIcon docKey="restore_backup" />
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            Restore is <strong>destructive</strong>. Existing data in the same tables will be replaced.
            Type the cluster name <strong>{selectedCluster?.name}</strong> to confirm.
          </Alert>
          <FormControlLabel
            control={
              <Switch checked={restoreDialog.downloadFirst}
                      onChange={(e) => setRestoreDialog({ ...restoreDialog, downloadFirst: e.target.checked })} />
            }
            label="Download from S3 first (needed if only a remote copy exists)"
          />
          <TextField
            fullWidth sx={{ mt: 2 }}
            label={`Type "${selectedCluster?.name}" to confirm`}
            value={restoreDialog.confirm}
            onChange={(e) => setRestoreDialog({ ...restoreDialog, confirm: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialog({ ...restoreDialog, open: false })}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={doRestore}
                  disabled={restoreDialog.confirm !== selectedCluster?.name}>
            Restore
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ ...deleteDialog, open: false })}>
        <DialogTitle>Delete backup: {deleteDialog.name}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This removes the backup and frees disk. Cannot be undone.
          </Alert>
          <FormControlLabel
            control={<Switch checked={deleteDialog.local}
                             onChange={(e) => setDeleteDialog({ ...deleteDialog, local: e.target.checked })} />}
            label="Delete local copy"
          />
          <FormControlLabel
            control={<Switch checked={deleteDialog.remote}
                             onChange={(e) => setDeleteDialog({ ...deleteDialog, remote: e.target.checked })} />}
            label="Delete remote (S3) copy"
          />
          <TextField
            fullWidth sx={{ mt: 2 }}
            label={`Type "${selectedCluster?.name}" to confirm`}
            value={deleteDialog.confirm}
            onChange={(e) => setDeleteDialog({ ...deleteDialog, confirm: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ ...deleteDialog, open: false })}>Cancel</Button>
          <Button color="error" variant="contained" onClick={doDelete}
                  disabled={deleteDialog.confirm !== selectedCluster?.name}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClickHouseBackupsTab;
